import { ReactNode } from 'react';
import {
	Anchor,
	Badge,
	Image,
	Burger,
	Button,
	createTheme,
	Divider,
	Group,
	MantineProvider as Mantine,
	MantineThemeOverride,
	Menu,
	Modal,
	NativeSelect,
	Pagination,
	Radio,
	RadioGroup,
	Select,
	Tooltip,
	Notification,
	Stack,
	Checkbox,
	Popover,
	Accordion,
	BackgroundImage,
	Title,
	Drawer,
	Highlight,
	RangeSlider,
	Slider,
	Progress,
	Overlay,
	LoadingOverlay,
	PasswordInput,
	Text,
	Textarea,
	TextInput,
	Loader as MantineLoader,
	NumberInput,
	SegmentedControl,
	Chip,
	Table,
	MantineColorsTuple,
	Card,
} from '@mantine/core';
import APP_CONFIG from '@/configs/appConfig';
import styles from '../sass/mantine.module.scss';
import '@mantine/core/styles.layer.css';
import 'dayjs/locale/pl';

const blue: MantineColorsTuple = [
	'#e9f2fb',
	'#d9e8f5',
	'#b2cee8',
	'#88b2db',
	'#669bd1',
	'#508dcb',
	'#4386c9',
	'#3473b2',
	'#2a68a3',
	'#17588f',
];

const components: MantineThemeOverride['components'] = {
	Chip: Chip.extend({
		defaultProps: {},
		classNames: {
			label: styles.chipLabel,
		},
	}),
	Pagination: Pagination.extend({
		defaultProps: {},
		classNames: {
			control: styles.paginationControl,
			dots: styles.paginationDots,
		},
	}),
	Text: Text.extend({
		defaultProps: {},
	}),
	Burger: Burger.extend({
		defaultProps: {},
	}),
	Badge: Badge.extend({
		defaultProps: {},
		classNames: {
			label: styles.badgeLabel,
		},
	}),
	Modal: Modal.extend({
		defaultProps: {
			radius: 0,
			centered: true,
			zIndex: APP_CONFIG.Z_INDEX.MODAL,
		},
		classNames: {
			root: styles.modalRoot,
			inner: styles.modalInner,
			content: styles.modalContent,
			header: styles.modalHeader,
			body: styles.modalBody,
			close: styles.modalClose,
			title: styles.modalTitle,
		},
	}),
	SegmentedControl: SegmentedControl.extend({
		defaultProps: {},
		classNames: {
			root: styles.segmentedControlRoot,
			label: styles.segmentedControlLabel,
		},
	}),
	Button: Button.extend({
		defaultProps: {
			radius: 0,
		},
	}),
	Menu: Menu.extend({
		defaultProps: {},
		classNames: {},
	}),
	Divider: Divider.extend({
		defaultProps: {},
		classNames: {
			label: styles.dividerLabel,
		},
	}),
	Select: Select.extend({
		defaultProps: {},
		classNames: {
			input: styles.selectInput,
			label: styles.selectLabel,
			option: styles.selectOption,
			dropdown: styles.selectDropdown,
			empty: styles.selectEmpty,
		},
	}),
	Image: Image.extend({
		defaultProps: {},
		classNames: {
			root: 'fade-in',
		},
	}),
	NativeSelect: NativeSelect.extend({
		defaultProps: {},
		classNames: {
			input: styles.selectInput,
			section: styles.selectRightSection,
		},
	}),
	Card: Card.extend({
		defaultProps: {
			withBorder: true,
			radius: 0,
			shadow: 'sm',
		},
		classNames: {},
	}),
	Tooltip: Tooltip.extend({
		defaultProps: {},
		classNames: {
			tooltip: styles.tooltipTooltip,
		},
	}),
	Group: Group.extend({
		defaultProps: {},
		classNames: {
			root: styles.groupRoot,
		},
	}),
	RadioGroup: RadioGroup.extend({ defaultProps: {} }),
	Radio: Radio.extend({
		defaultProps: {},
		classNames: {
			root: styles.radioRoot,
			body: styles.radioBody,
			labelWrapper: styles.radioLabelWrapper,
			label: styles.radioLabel,
		},
	}),
	Anchor: Anchor.extend({
		defaultProps: {},
	}),
	Stack: Stack.extend({
		defaultProps: {},
		classNames: {
			root: styles.stackRoot,
		},
	}),
	Checkbox: Checkbox.extend({
		classNames: {
			inner: styles.checkboxInner,
			label: styles.checkboxLabel,
		},
		defaultProps: {},
	}),
	TextInput: TextInput.extend({
		defaultProps: {
			radius: 0,
			withAsterisk: false,
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	NumberInput: NumberInput.extend({
		defaultProps: {
			radius: 0,
		},
		classNames: {
			input: styles.textInputInput,
			label: styles.textInputLabel,
			error: styles.textInputError,
		},
	}),
	Textarea: Textarea.extend({
		defaultProps: {},
		classNames: {
			label: styles.textInputLabel,
		},
	}),
	PasswordInput: PasswordInput.extend({
		defaultProps: {},
		classNames: {
			wrapper: styles.passwordInputWrapper,
			input: styles.passwordInputInput,
			section: styles.passwordInputSection,
			error: styles.passwordInputError,
		},
	}),
	Loader: MantineLoader.extend({
		defaultProps: {},
	}),
	LoadingOverlay: LoadingOverlay.extend({
		defaultProps: {},
	}),
	Overlay: Overlay.extend({
		defaultProps: {},
	}),
	Progress: Progress.extend({
		defaultProps: {},
		styles: ({ colors }) => ({
			root: {
				backgroundColor: colors.green[0],
			},
		}),
	}),
	Slider: Slider.extend({
		defaultProps: {},
		classNames: {
			root: styles.sliderRoot,
			bar: styles.sliderBar,
			thumb: styles.sliderThumb,
			track: styles.sliderTrack,
			label: styles.sliderLabel,
			markLabel: styles.sliderMarkLabel,
		},
	}),
	RangeSlider: RangeSlider.extend({
		defaultProps: {},
	}),
	Highlight: Highlight.extend({
		defaultProps: {},
	}),
	Table: Table.extend({
		defaultProps: {},
		classNames: {},
	}),
	Drawer: Drawer.extend({
		defaultProps: {},
		classNames: {
			overlay: styles.drawerInner,
			content: styles.drawerContent,
			inner: styles.drawerInner,
			body: styles.drawerBody,
			header: styles.drawerHeader,
			title: styles.drawerTitle,
			close: styles.drawerClose,
		},
	}),
	Title: Title.extend({
		defaultProps: {},
	}),
	Notification: Notification.extend({
		defaultProps: {},
		styles: {
			root: {
				zIndex: APP_CONFIG.Z_INDEX.NOTIFICATION,
			},
		},
		classNames: {
			root: styles.notificationRoot,
			icon: styles.notificationIcon,
			closeButton: styles.notificationCloseButton,
		},
	}),
	BackgroundImage: BackgroundImage.extend({
		defaultProps: {},
	}),
	Accordion: Accordion.extend({
		defaultProps: {},
		classNames: {
			content: styles.accordionContent,
			item: styles.accordionItem,
			control: styles.accordionControl,
			label: styles.accordionLabel,
			chevron: styles.accordionChevron,
		},
	}),
	Popover: Popover.extend({
		defaultProps: {},
		classNames: {
			dropdown: styles.popoverDropdown,
		},
	}),
};

const headings: MantineThemeOverride['headings'] = {
	fontWeight: '600',
	sizes: {
		h1: {
			fontSize: '36px',
			fontWeight: '700',
		},
		h2: {
			fontSize: '24px',
			fontWeight: '700',
		},
	},
};

const other: MantineThemeOverride['other'] = {};

const breakpoints = Object.entries(APP_CONFIG.BREAKPOINTS).reduce<
	MantineThemeOverride['breakpoints']
>((acc, curr) => {
	acc![curr[0].toLowerCase()] = curr[1] as string;
	return acc;
}, {});

export const MantineProvider = ({ children }: { children: ReactNode }) => {
	const theme = createTheme({
		fontFamily: 'Geist',
		white: '#ffffff',
		black: '#354d63',
		colors: {
			blue,
		},
		other,
		cursorType: 'pointer',
		components,
		headings,
		breakpoints,
		primaryColor: 'blue',
		primaryShade: 8,
	});

	return <Mantine theme={theme}>{children}</Mantine>;
};
